import {
  _bind_instance_methods,
  _each,
  _extend,
  _includes,
  _isFunction,
  _isUndefined,
  _register_event,
  _safewrap_instance_methods,
  logger,
} from "./utils";
import {
  getClassName,
  getSafeText,
  isElementNode,
  isSensitiveElement,
  isTag,
  isTextNode,
  shouldCaptureDomEvent,
  shouldCaptureElement,
  shouldCaptureValue,
  autocaptureCompatibleElements,
  isAngularStyleAttr,
  isDocumentFragment,
} from "./autocapture-utils";
import RageClick from "./extensions/rageclick";
import {
  AutocaptureConfig,
  AutoCaptureCustomProperty,
  DecideResponse,
  Properties,
} from "./types";
import { AuthingInsight } from "./posthog-core";

//++++++++++++++++++++++++++++++++++++++++++>
var matchOperatorsRe = /[|\\{}()[\]^$+*?.]/g;

function escapeStringRegexp(str: any) {
  if (typeof str !== "string") {
    throw new TypeError("Expected a string");
  }
  return str.replace(matchOperatorsRe, "\\$&");
}
const regexpCache = new Map();

function makeRegexp(pattern: any, options: any) {
  options = {
    caseSensitive: false,
    ...options,
  };

  const cacheKey = pattern + JSON.stringify(options);

  if (regexpCache.has(cacheKey)) {
    return regexpCache.get(cacheKey);
  }

  const negated = pattern[0] === "!";

  if (negated) {
    pattern = pattern.slice(1);
  }

  pattern = escapeStringRegexp(pattern).replace(/\\\*/g, "[\\s\\S]*");

  const regexp = new RegExp(`^${pattern}$`, options.caseSensitive ? "" : "i");
  (regexp as any).negated = negated;
  regexpCache.set(cacheKey, regexp);

  return regexp;
}

function isMatch(input: any, pattern: any, options?: any) {
  const inputArray = Array.isArray(input) ? input : [input];
  const patternArray = Array.isArray(pattern) ? pattern : [pattern];

  return inputArray.some((input) => {
    return patternArray.every((pattern) => {
      const regexp = makeRegexp(pattern, options);
      const matches = regexp.test(input);
      return regexp.negated ? !matches : matches;
    });
  });
}
//++++++++++++++++++++++++++++++++++++++++++>

const autocapture = {
  _initializedTokens: [] as string[],

  _previousElementSibling: function (el: Element): Element | null {
    if (el.previousElementSibling) {
      return el.previousElementSibling;
    } else {
      let _el: Element | null = el;
      do {
        _el = _el.previousSibling as Element | null; // resolves to ChildNode->Node, which is Element's parent class
      } while (_el && !isElementNode(_el));
      return _el;
    }
  },

  _getAugmentPropertiesFromElement: function (elem: Element): Properties {
    const shouldCaptureEl = shouldCaptureElement(elem);
    if (!shouldCaptureEl) {
      return {};
    }

    const props: Properties = {};

    _each(elem.attributes, function (attr: Attr) {
      if (attr.name.startsWith("data-ph-capture-attribute")) {
        const propertyKey = attr.name.replace("data-ph-capture-attribute-", "");
        const propertyValue = attr.value;
        if (propertyKey && propertyValue && shouldCaptureValue(propertyValue)) {
          props[propertyKey] = propertyValue;
        }
      }
    });
    return props;
  },

  _getPropertiesFromElement: function (
    elem: Element,
    maskInputs: boolean,
    maskText: boolean
  ): Properties {
    const tag_name = elem.tagName.toLowerCase();
    const props: Properties = {
      tag_name: tag_name,
    };
    if (autocaptureCompatibleElements.indexOf(tag_name) > -1 && !maskText) {
      props["$el_text"] = getSafeText(elem);
    }

    const classes = getClassName(elem);
    if (classes.length > 0)
      props["classes"] = classes.split(" ").filter(function (c) {
        return c !== "";
      });

    _each(elem.attributes, function (attr: Attr) {
      // Only capture attributes we know are safe
      if (
        isSensitiveElement(elem) &&
        ["name", "id", "class"].indexOf(attr.name) === -1
      )
        return;

      if (
        !maskInputs &&
        shouldCaptureValue(attr.value) &&
        !isAngularStyleAttr(attr.name)
      ) {
        props["attr__" + attr.name] = attr.value;
      }
    });

    let nthChild = 1;
    let nthOfType = 1;
    let currentElem: Element | null = elem;
    while ((currentElem = this._previousElementSibling(currentElem))) {
      // eslint-disable-line no-cond-assign
      nthChild++;
      if (currentElem.tagName === elem.tagName) {
        nthOfType++;
      }
    }
    props["nth_child"] = nthChild;
    props["nth_of_type"] = nthOfType;

    return props;
  },

  _getDefaultProperties: function (eventType: string): Properties {
    return {
      $event_type: eventType,
      $ce_version: 1,
    };
  },

  _extractCustomPropertyValue: function (
    customProperty: AutoCaptureCustomProperty
  ): string {
    const propValues: string[] = [];
    _each(
      document.querySelectorAll(customProperty["css_selector"]),
      function (matchedElem) {
        let value;

        if (
          ["input", "select"].indexOf(matchedElem.tagName.toLowerCase()) > -1
        ) {
          value = matchedElem["value"];
        } else if (matchedElem["textContent"]) {
          value = matchedElem["textContent"];
        }

        if (shouldCaptureValue(value)) {
          propValues.push(value);
        }
      }
    );
    return propValues.join(", ");
  },

  // TODO: delete custom_properties after changeless typescript refactor
  _getCustomProperties: function (targetElementList: Element[]): Properties {
    const props: Properties = {}; // will be deleted
    _each(this._customProperties, (customProperty) => {
      _each(customProperty["event_selectors"], (eventSelector) => {
        const eventElements = document.querySelectorAll(eventSelector);
        _each(eventElements, (eventElement) => {
          if (
            _includes(targetElementList, eventElement) &&
            shouldCaptureElement(eventElement)
          ) {
            props[customProperty["name"]] =
              this._extractCustomPropertyValue(customProperty);
          }
        });
      });
    });
    return props;
  },

  _getEventTarget: function (e: Event): Element | null {
    // https://developer.mozilla.org/en-US/docs/Web/API/Event/target#Compatibility_notes
    if (typeof e.target === "undefined") {
      return (e.srcElement as Element) || null;
    } else {
      if ((e.target as HTMLElement)?.shadowRoot) {
        return (e.composedPath()[0] as Element) || null;
      }
      return (e.target as Element) || null;
    }
  },

  _autoCaptureAuthingUserInfo: function (_: Event, instance: AuthingInsight) {
    const _auto_capture_authing_user_info =
      instance?.config._auto_capture_authing_user_info || null;
    const _authing_open_api_host =
      instance?.config._authing_open_api_host || null;
    const _authing_userpool_id = instance?.config._authing_userpool_id || null;
    const _authing_access_token =
      window.localStorage.getItem("_authing_token") || null;

    const $device_id = instance.persistence.props.$device_id;

    const identified = instance._getAutoCaptureAuthingUserTag($device_id);

    if (
      instance &&
      _auto_capture_authing_user_info &&
      _authing_open_api_host &&
      _authing_userpool_id &&
      _authing_access_token
    ) {
      try {
        const $user_state = instance.persistence.props.$user_state;
        if (identified) {
          logger.log("Authing Insight 已经捕捉过信息了");
        }
        if ($user_state !== "identified" && !identified) {
          const _authing_get_user_info_uri = `${_authing_open_api_host}/api/v3/get-profile`;
          const _head = {
            ...instance.get_config("xhr_headers"),
            authorization: _authing_access_token,
            "x-authing-userpool-id": _authing_userpool_id,
          };
          instance.config["xhr_headers"] = _head;

          const _instance = instance;

          instance._send_request(
            _authing_get_user_info_uri,
            {},
            {
              method: "GET",
            },
            (response) => {
              if (response.statusCode === 200) {
                const user = response.data;
                _instance.identify(user.userId);
                _instance.people.set({
                  ...user,
                });
                _instance._setAutoCaptureAuthingUserTag($device_id, 1);
                logger.log("Authing Insight 已经自动合并用户信息");
              }
            }
          );
        } else {
          logger.log("Authing Insight 已经捕捉过信息了");
        }
      } catch (error) {
        logger.error(
          `获取 authing user info 报错 _auto_capture_authing_user_info : ${_auto_capture_authing_user_info}`
        );
        logger.error(
          `获取 authing user info 报错 _auto_capture_authing_user_info : ${_authing_open_api_host}`
        );
        logger.error(
          `获取 authing user info 报错 _auto_capture_authing_user_info : ${_authing_userpool_id}`
        );
        logger.error(`获取 authing user info 报错 xhr : ${error}`);
      }
    }
  },

  _captureEvent: function (
    e: Event,
    instance: AuthingInsight,
    eventName = "$autocapture"
  ): boolean | void {
    /*** Don't mess with this code without running IE8 tests on it ***/
    let target = this._getEventTarget(e);
    if (isTextNode(target)) {
      // defeat Safari bug (see: http://www.quirksmode.org/js/events_properties.html)
      target = (target.parentNode || null) as Element | null;
    }

    if (eventName === "$autocapture") {
      // 捕获用户信息
      this._autoCaptureAuthingUserInfo(e, instance);
      // 捕获元素
      this._autoCaptureElem(e, instance);
    }

    if (
      eventName === "$autocapture" &&
      e.type === "click" &&
      e instanceof MouseEvent
    ) {
      if (
        this.rageclicks?.isRageClick(e.clientX, e.clientY, new Date().getTime())
      ) {
        this._captureEvent(e, instance, "$rageclick");
      }
    }

    if (target && shouldCaptureDomEvent(target, e, this.config)) {
      const targetElementList = [target];
      let curEl = target;
      while (curEl.parentNode && !isTag(curEl, "body")) {
        if (isDocumentFragment(curEl.parentNode)) {
          targetElementList.push((curEl.parentNode as any).host);
          curEl = (curEl.parentNode as any).host;
          continue;
        }
        targetElementList.push(curEl.parentNode as Element);
        curEl = curEl.parentNode as Element;
      }

      const elementsJson: Properties[] = [];
      const autocaptureAugmentProperties: Properties = {};
      let href,
        explicitNoCapture = false;
      _each(targetElementList, (el) => {
        const shouldCaptureEl = shouldCaptureElement(el);

        // if the element or a parent element is an anchor tag
        // include the href as a property
        if (el.tagName.toLowerCase() === "a") {
          href = el.getAttribute("href");
          href = shouldCaptureEl && shouldCaptureValue(href) && href;
        }

        // allow users to programmatically prevent capturing of elements by adding class 'ph-no-capture'
        const classes = getClassName(el).split(" ");
        if (_includes(classes, "ph-no-capture")) {
          explicitNoCapture = true;
        }

        elementsJson.push(
          this._getPropertiesFromElement(
            el,
            instance.get_config("mask_all_element_attributes"),
            instance.get_config("mask_all_text")
          )
        );

        const augmentProperties = this._getAugmentPropertiesFromElement(el);
        _extend(autocaptureAugmentProperties, augmentProperties);
      });

      if (!instance.get_config("mask_all_text")) {
        elementsJson[0]["$el_text"] = getSafeText(target);
      }

      if (href) {
        elementsJson[0]["attr__href"] = href;
      }

      if (explicitNoCapture) {
        return false;
      }

      const props = _extend(
        this._getDefaultProperties(e.type),
        {
          $elements: elementsJson,
        },
        this._getCustomProperties(targetElementList),
        autocaptureAugmentProperties
      );

      instance.capture(eventName, props);
      return true;
    }
  },

  _autoCaptureElem: function (_: Event, instance: AuthingInsight) {
    if (window) {
      const config = instance.config.autoCaptureElemConfig || [];
      const current_url = window.location.href;

      let captureData: any = {};

      config.forEach((item: any) => {
        const urlPath = item.urlPath;
        const elements = item.elements;
        const eventName = item.eventName
          ? `🕹️ ${item.eventName}`
          : "🕹️ 自定义捕获页面元素";

        const currentDomain = `${window.location.protocol}//${document.location.host}`;
        const patterns = `${currentDomain}${urlPath}`;
        const isMatch1 = patterns.includes("*");
        const isMatch2 = isMatch(current_url, patterns);
        const isMatch3 = isMatch1 && isMatch2;

        if (urlPath === "/*" || current_url === urlPath || isMatch3) {
          elements.forEach((data: any) => {
            const selector = data.selector;
            const el = document.querySelector(selector);
            const tag = `${data.propertiesName}`;
            const val = el?.innerHTML;

            // 开启捕获用户输入事件的值
            if (!!data.captureChangeEvt && _.type === "change") {
              const $currentDom = _.target as Node;
              if ($currentDom && $currentDom.isEqualNode(el)) {
                const inputVal = el.value;
                captureData[`${tag}`] = inputVal;
                // 单独发送一个 captureChangeEvt
                instance.capture("🕹️ 自定义捕获用户输入事件", captureData);
              }
            } else if (el) {
              // 自定义捕获页面元素
              captureData[`${tag}`] = val;
            }
          });

          if (Object.keys(captureData).length > 0) {
            instance.capture(eventName, captureData);
          } else {
            logger.error(`${urlPath} :没有找到对应的元素`);
          }
        } else {
          logger.error(
            `当前的 url 不符合您配置的规则 current:${current_url} ,urlPath: ${urlPath}`
          );
        }
      });
    }
  },

  // only reason is to stub for unit tests
  // since you can't override window.location props
  _navigate: function (href: string): void {
    window.location.href = href;
  },

  _addDomEventHandlers: function (instance: AuthingInsight): void {
    const handler = (e: Event) => {
      e = e || window.event;
      this._captureEvent(e, instance);
    };
    _register_event(document, "submit", handler, false, true);
    _register_event(document, "change", handler, false, true);
    _register_event(document, "click", handler, false, true);
  },

  _customProperties: [] as AutoCaptureCustomProperty[],
  rageclicks: null as RageClick | null,
  config: undefined as AutocaptureConfig | undefined,

  init: function (instance: AuthingInsight): void {
    if (typeof instance.__autocapture !== "boolean") {
      this.config = instance.__autocapture;
    }

    // precompile the regex
    if (this.config?.url_allowlist) {
      this.config.url_allowlist = this.config.url_allowlist.map(
        (url) => new RegExp(url)
      );
    }

    this.rageclicks = new RageClick(instance.get_config("rageclick"));
  },

  afterDecideResponse: function (
    response: DecideResponse,
    instance: AuthingInsight
  ): void {
    const token = instance.get_config("token");
    if (this._initializedTokens.indexOf(token) > -1) {
      logger.log('autocapture already initialized for token "' + token + '"');
      return;
    }

    this._initializedTokens.push(token);

    if (
      response &&
      response["config"] &&
      response["config"]["enable_collect_everything"] &&
      instance.get_config("autocapture")
    ) {
      // TODO: delete custom_properties after changeless typescript refactor
      if (response["custom_properties"]) {
        this._customProperties = response["custom_properties"];
      }
      this._addDomEventHandlers(instance);
    } else {
      instance["__autocapture"] = false;
    }
  },

  // this is a mechanism to ramp up CE with no server-side interaction.
  // when CE is active, every page load results in a decide request. we
  // need to gently ramp this up so we don't overload decide. this decides
  // deterministically if CE is enabled for this project by modding the char
  // value of the project token.
  enabledForProject: function (
    token: string | null | undefined,
    numBuckets: number,
    numEnabledBuckets: number
  ): boolean {
    if (!token) {
      return true;
    }
    numBuckets = !_isUndefined(numBuckets) ? numBuckets : 10;
    numEnabledBuckets = !_isUndefined(numEnabledBuckets)
      ? numEnabledBuckets
      : 10;
    let charCodeSum = 0;
    for (let i = 0; i < token.length; i++) {
      charCodeSum += token.charCodeAt(i);
    }
    return charCodeSum % numBuckets < numEnabledBuckets;
  },

  isBrowserSupported: function (): boolean {
    return _isFunction(document.querySelectorAll);
  },
};

_bind_instance_methods(autocapture);
_safewrap_instance_methods(autocapture);

export { autocapture, isMatch };
